import { useMemo } from "react";
import { useAppContext } from "src/hooks/useAppContext";
import type { PixiBillingClient } from "./pixiBillingClient";
import { createV1PixiBillingClient } from "./v1PixiBillingClient";
import { createV2PixiBillingClient } from "./v2PixiBillingClient";

/**
 * The PIXI billing client for an org: billing v2 for v2 orgs, billing v1 for everyone else.
 *
 * @remarks
 * Anything other than `"v2"` (v1, or the version still loading) is v1, so v1 orgs keep today's calls.
 */
export const pixiBillingClientFor = (
  billingVersion: string | null | undefined,
  spaceId?: string,
  currencyCode?: string | null,
): PixiBillingClient =>
  billingVersion === "v2"
    ? createV2PixiBillingClient({ currencyCode })
    : createV1PixiBillingClient(spaceId);

/**
 * @returns Null while the billing version loads, so nothing is fetched or bought through the wrong billing.
 */
export const usePixiBillingClient = (): PixiBillingClient | null => {
  const context = useAppContext();
  const isVersionPending = context?.billingVersion === null;
  const isV2 = context?.billingVersion === "v2";
  const spaceId = context?.currentSpace?.id;
  // Only v2 reads it: the currency of the billing v2 account already loaded at app start.
  const currencyCode = isV2
    ? context?.billingV2Account?.subscription.currencyCode
    : undefined;
  return useMemo(
    () =>
      isVersionPending
        ? null
        : pixiBillingClientFor(isV2 ? "v2" : "v1", spaceId, currencyCode),
    [isVersionPending, isV2, spaceId, currencyCode],
  );
};
